/** Documento adjunto a una solicitud (crudo, como lo manda el backend). */
export interface SellerDocument {
  id?: number;
  doc_type?: string;
  type?: string;
  documentType?: string;
  file_url?: string;
  url?: string;
  original_name?: string;
  name?: string;
  fileName?: string;
  status?: string;
  rejection_reason?: string;
  reason?: string;
  required?: boolean;
  version?: number;
  label?: string;
  [key: string]: unknown;
}

/** Revisión registrada por un admin sobre una solicitud (forma cruda). */
export interface ApplicationReview {
  id?: number;
  adminUserId?: number;
  adminName?: string;
  action?: string;
  status?: string;
  notes?: string;
  reason?: string;
  createdAt?: string;
  [key: string]: unknown;
}

/** Solicitud de registro de proveedor/vendedor (fila de lista, camelCase). */
export interface SellerApplication {
  id: number;
  folio?: string;
  status?: string;
  userId?: number;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  legalName?: string;
  rfc?: string;
  rfcType?: string;
  clabe?: string;
  bankName?: string;
  legalEntity?: string;
  documentCount?: number;
  rejectedDocuments?: number;
  /** Quién debe actuar: `admin` (revisar), `applicant` (corregir/concluir) o `none`. */
  waitingOn?: 'admin' | 'applicant' | 'none';
  /** Días desde el último movimiento. */
  waitingDays?: number | null;
  /** Lleva más del plazo de revisión esperando a un admin. */
  isOverdue?: boolean;
  fiscalAddress?: Record<string, unknown> | string;
  documents?: SellerDocument[];
  reviews?: ApplicationReview[];
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

// ---------- Vistas normalizadas del DETALLE ----------
// El backend responde `data: { application, documents, requiredDocuments, history }`
// con la solicitud en snake_case. Se normaliza a estas vistas limpias.

/** Documento listo para render (con URL de imagen y etiqueta en español). */
export interface SellerDocumentView {
  id?: number;
  docType: string;
  label: string;
  fileUrl: string;
  status: string;
  rejectionReason?: string;
  required: boolean;
  originalName?: string;
  version?: number;
}

/** Evento del historial de revisión, normalizado. */
export interface ApplicationHistoryView {
  id?: number;
  action: string;
  notes?: string;
  adminName?: string;
  createdAt?: string;
}

/** Detalle completo de una solicitud, ya normalizado para la pantalla. */
export interface SellerApplicationDetail {
  id: number;
  status: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  userId?: number | null;
  rfc?: string;
  rfcType?: string;
  rfcValid?: boolean;
  businessName?: string;
  taxRegime?: string;
  fiscalAddress?: Record<string, unknown> | string | null;
  clabe?: string;
  bankCode?: string;
  bankName?: string;
  accountHolder?: string;
  clabeValid?: boolean;
  rejectReason?: string | null;
  correctionsNotes?: string | null;
  createdAt?: string;
  updatedAt?: string;
  documents: SellerDocumentView[];
  requiredDocuments: { docType: string; label: string }[];
  history: ApplicationHistoryView[];
}

function bool(v: unknown): boolean {
  return v === true || v === 1 || v === '1';
}

/** Normaliza `data` de `GET /sellers/admin/applications/:id` a `SellerApplicationDetail`. */
export function normalizeApplicationDetail(data: unknown): SellerApplicationDetail | null {
  // Se usa `any` a propósito: la respuesta cruda del backend es dinámica
  // (snake_case, forma anidada o plana) y aquí se normaliza a tipos limpios.
  const d: any = data;
  if (!d) return null;
  // Tolera tanto la forma anidada { application, documents, ... } como una plana.
  const a: any = d.application ?? d;
  if (!a) return null;

  const documentsRaw: SellerDocument[] = d.documents ?? a.documents ?? [];
  const historyRaw: any[] = d.history ?? a.history ?? a.reviews ?? [];
  const requiredRaw: any[] = d.requiredDocuments ?? [];

  return {
    id: Number(a.id),
    status: a.status ?? 'pending',
    firstName: a.first_name ?? a.firstName,
    lastName: a.last_name ?? a.lastName,
    email: a.email,
    phone: a.phone,
    userId: a.user_id ?? a.userId ?? null,
    rfc: a.rfc,
    rfcType: a.rfc_type ?? a.rfcType,
    rfcValid: bool(a.rfc_valid ?? a.rfcValid),
    businessName: a.business_name ?? a.businessName,
    taxRegime: a.tax_regime ?? a.taxRegime,
    fiscalAddress: a.fiscal_address ?? a.fiscalAddress ?? null,
    clabe: a.clabe,
    bankCode: a.bank_code ?? a.bankCode,
    bankName: a.bank_name ?? a.bankName,
    accountHolder: a.account_holder ?? a.accountHolder,
    clabeValid: bool(a.clabe_valid ?? a.clabeValid),
    rejectReason: a.reject_reason ?? a.rejectReason ?? null,
    correctionsNotes: a.corrections_notes ?? a.correctionsNotes ?? null,
    createdAt: a.created_at ?? a.createdAt,
    updatedAt: a.updated_at ?? a.updatedAt,
    documents: documentsRaw.map((doc) => ({
      id: doc.id,
      docType: String(doc.doc_type ?? doc.type ?? doc.documentType ?? ''),
      label: doc.label ?? String(doc.doc_type ?? doc.type ?? 'Documento'),
      fileUrl: doc.file_url ?? doc.url ?? '',
      status: doc.status ?? 'pending',
      rejectionReason: doc.rejection_reason ?? doc.reason ?? undefined,
      required: bool(doc.required),
      originalName: doc.original_name ?? doc.name ?? doc.fileName,
      version: doc.version,
    })),
    requiredDocuments: requiredRaw.map((r) => ({
      docType: String(r.docType ?? r.doc_type ?? ''),
      label: r.label ?? String(r.docType ?? ''),
    })),
    history: historyRaw.map((h) => ({
      id: h.id,
      action: String(h.action ?? h.status ?? ''),
      notes: h.notes ?? h.reason ?? undefined,
      adminName:
        (h.admin && typeof h.admin === 'object' ? h.admin.name : undefined) ??
        h.adminName ??
        undefined,
      createdAt: h.createdAt ?? h.created_at,
    })),
  };
}

/** Desempeño agregado de un vendedor (spec 2.3). */
export interface SellerPerformance {
  sales?: { count: number; amount: number };
  refunds?: { count: number; amount: number };
  cancellations?: number;
  avgShippingHours?: number;
  rating?: number;
  claims?: number;
  [key: string]: unknown;
}
